import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiChevronDown, FiX, FiSearch, FiPlus, FiColumns } from 'react-icons/fi';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import Navbar from './Navbar';

const ConversionPage = () => {
    const [dateRange, setDateRange] = useState('today');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [authChecked, setAuthChecked] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showOfferDropdown, setShowOfferDropdown] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState([]);
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const [allOffers, setAllOffers] = useState([]);
    const navigate = useNavigate();

    // All possible columns from your database
    const allColumns = [
        { id: 'tid', label: 'Transaction ID' },
        { id: 'offerId', label: 'Offer ID' },
        { id: 'user', label: 'User' },
        { id: 'refer', label: 'Referrer' },
        { id: 'event', label: 'Event' },
        { id: 'click', label: 'Click' },
        { id: 'conversion', label: 'Conversion' },
        { id: 'convTime', label: 'Conversion Time' },
        { id: 'clickTime', label: 'Click Time' },
        { id: 'deviceIP', label: 'IP Address' },
        { id: 'deviceID', label: 'Device ID' },
        { id: 'platform', label: 'Platform' },
        { id: 'browser', label: 'Browser' },
        { id: 'userAgent', label: 'User Agent' },
        { id: 'country', label: 'Country' },
        { id: 'region', label: 'Region' },
        { id: 'city', label: 'City' },
        { id: 'source', label: 'Source' },
        { id: 'referrer', label: 'Referrer URL' },
        { id: 'campaign', label: 'Campaign' },
        { id: 'deviceType', label: 'Device Type' },
        { id: 'connectionType', label: 'Connection Type' },
        { id: 'isSelfRefer', label: 'Self Referral' },
        { id: 'payoutUser', label: 'User Payout' },
        { id: 'payoutRefer', label: 'Referrer Payout' },
        { id: 'createdAt', label: 'Created At' }
    ];

    // Default visible columns
    const [visibleColumns, setVisibleColumns] = useState([
        'offerId', 'user', 'refer', 'event', 'clickTime',
        'conversion', 'convTime'
    ]);

    const [tableData, setTableData] = useState([]);

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    const checkAuth = async () => {
        try {
            const token = getCookie('offerAdminAuth');
            if (!token) {
                navigate('/adminBT/login');
                return false;
            }

            const res = await axios.post('https://offers.cashbolt.in.net/server/', {
                action: 'checkAuth',
                authToken: token
            });

            if (!res.data.status) {
                navigate('/adminBT/login');
                return false;
            }
            return true;
        } catch (error) {
            navigate('/adminBT/login');
            return false;
        }
    };

    const fetchAllOffers = async () => {
        try {
            const token = getCookie('offerAdminAuth');
            const response = await axios.post('https://offers.cashbolt.in.net/server/', {
                action: 'getAllOffers',
                authToken: token
            });

            if (response.data.status) {
                setAllOffers(response.data.data);
            } else {
                console.error('Failed to fetch offers:', response.data.message);
            }
        } catch (error) {
            console.error("Error fetching offers:", error);
        }
    };

    const fetchTableData = async () => {
        if (!authChecked) return;

        setLoading(true);
        try {
            const token = getCookie('offerAdminAuth');
            const formattedStart = startDate.toISOString().split('T')[0];
            const formattedEnd = endDate.toISOString().split('T')[0];

            const response = await axios.post('https://offers.cashbolt.in.net/server/', {
                action: 'getConversionReport',
                authToken: token,
                startDate: formattedStart,
                endDate: formattedEnd,
                offerIds: selectedOffers.map(o => o.offerId)
            });

            if (response.data.status) {
                setTableData(response.data.data);
            } else {
                console.error('Failed to fetch report data:', response.data.message);
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const verifyAuth = async () => {
            const isAuthenticated = await checkAuth();
            setAuthChecked(isAuthenticated);
            if (isAuthenticated) {
                await fetchAllOffers();
            }
        };
        verifyAuth();
    }, []);

    useEffect(() => {
        if (authChecked) {
            fetchTableData();
        }
    }, [authChecked, selectedOffers, dateRange, startDate, endDate]);

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        setShowDatePicker(false);

        const today = new Date();
        const newDates = {
            start: new Date(),
            end: new Date()
        };

        switch (range) {
            case 'today':
                break;
            case 'yesterday':
                newDates.start.setDate(today.getDate() - 1);
                newDates.end.setDate(today.getDate() - 1);
                break;
            case 'week':
                newDates.start.setDate(today.getDate() - 7);
                break;
            case 'month':
                newDates.start = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            default:
                break;
        }

        setStartDate(newDates.start);
        setEndDate(newDates.end);
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const filteredOffers = allOffers.filter(offer =>
        offer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        offer.offerId.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleOfferSelection = (offer) => {
        if (selectedOffers.some(o => o.id === offer.id)) {
            setSelectedOffers(selectedOffers.filter(o => o.id !== offer.id));
        } else {
            setSelectedOffers([...selectedOffers, offer]);
        }
    };

    const removeSelectedOffer = (offerId) => {
        setSelectedOffers(selectedOffers.filter(o => o.id !== offerId));
    };

    const toggleColumnVisibility = (columnId) => {
        if (visibleColumns.includes(columnId)) {
            setVisibleColumns(visibleColumns.filter(col => col !== columnId));
        } else {
            setVisibleColumns([...visibleColumns, columnId]);
        }
    };


    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <Navbar />
            {/* Header */}
            <header className="admin-header">
                <h1>Conversion Report</h1>

                <div className="filters-container">
                    {/* Date Range Picker - Moved to first position */}
                    <div className="filter-group date-filter-group">
                        <div className="date-selector-container">
                            <button
                                className="date-range-btn"
                                onClick={() => setShowDatePicker(!showDatePicker)}
                            >
                                <FiCalendar className="calendar-icon" />
                                <span>
                                    {dateRange === 'today' && 'Today'}
                                    {dateRange === 'yesterday' && 'Yesterday'}
                                    {dateRange === 'week' && 'Last 7 Days'}
                                    {dateRange === 'month' && 'This Month'}
                                    {dateRange === 'custom' && `${formatDate(startDate)} - ${formatDate(endDate)}`}
                                </span>
                                <FiChevronDown className={`dropdown-chevron ${showDatePicker ? 'open' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {showDatePicker && (
                                    <motion.div
                                        className="date-picker-dropdown"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <div className="date-options-container">
                                            <div
                                                className={`date-option ${dateRange === 'today' ? 'active' : ''}`}
                                                onClick={() => handleDateRangeChange('today')}
                                            >
                                                Today
                                            </div>
                                            <div
                                                className={`date-option ${dateRange === 'yesterday' ? 'active' : ''}`}
                                                onClick={() => handleDateRangeChange('yesterday')}
                                            >
                                                Yesterday
                                            </div>
                                            <div
                                                className={`date-option ${dateRange === 'week' ? 'active' : ''}`}
                                                onClick={() => handleDateRangeChange('week')}
                                            >
                                                Last 7 Days
                                            </div>
                                            <div
                                                className={`date-option ${dateRange === 'month' ? 'active' : ''}`}
                                                onClick={() => handleDateRangeChange('month')}
                                            >
                                                This Month
                                            </div>
                                        </div>
                                        <div className="custom-date-section">
                                            <div className="custom-date-label">Custom Range:</div>
                                            <div className="custom-date-inputs">
                                                <input
                                                    type="date"
                                                    value={startDate.toISOString().split('T')[0]}
                                                    onChange={(e) => {
                                                        setStartDate(new Date(e.target.value));
                                                        setDateRange('custom');
                                                    }}
                                                />
                                                <span className="date-separator">to</span>
                                                <input
                                                    type="date"
                                                    value={endDate.toISOString().split('T')[0]}
                                                    onChange={(e) => {
                                                        setEndDate(new Date(e.target.value));
                                                        setDateRange('custom');
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Offer Filter - Improved styling */}
                    <div className="filter-group offer-filter-group">
                        <div className="offer-filter-container">
                            <div className="offer-search-container">
                                <FiSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search offers..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => setShowOfferDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowOfferDropdown(false), 200)}
                                />
                                <FiChevronDown className={`dropdown-chevron ${showOfferDropdown ? 'open' : ''}`} />

                                <AnimatePresence>
                                    {showOfferDropdown && (
                                        <motion.div
                                            className="offer-dropdown"
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {filteredOffers.length > 0 ? (
                                                filteredOffers.map(offer => (
                                                    <div
                                                        key={offer.id}
                                                        className={`offer-option ${selectedOffers.some(o => o.id === offer.id) ? 'selected' : ''}`}
                                                        onMouseDown={() => toggleOfferSelection(offer)}
                                                    >
                                                        {offer.name} <span className="offer-id">({offer.offerId})</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-results">No offers found</div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="selected-offers-tags">
                                {selectedOffers.map(offer => (
                                    <div key={offer.id} className="offer-tag">
                                        {offer.name}
                                        <button
                                            className="remove-tag"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                removeSelectedOffer(offer.id);
                                            }}
                                        >
                                            <FiX size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Column Selector - Wider dropdown */}
                    <div className="filter-group column-filter-group">
                        <div className="column-selector-container">
                            <button
                                className="column-selector-btn"
                                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                            >
                                <FiColumns className="columns-icon" />
                                <span>Columns</span>
                                <FiChevronDown className={`dropdown-chevron ${showColumnDropdown ? 'open' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {showColumnDropdown && (
                                    <motion.div
                                        className="column-dropdown"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <div className="column-options-grid">
                                            {allColumns.map(column => (
                                                <div
                                                    key={column.id}
                                                    className={`column-option ${visibleColumns.includes(column.id) ? 'selected' : ''}`}
                                                    onClick={() => toggleColumnVisibility(column.id)}
                                                >
                                                    <div className="column-checkbox">
                                                        {visibleColumns.includes(column.id) && (
                                                            <div className="checkbox-indicator" />
                                                        )}
                                                    </div>
                                                    {column.label}
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </header>

            {/* Data Table */}
            <div className="offers-table-container">
                <table className="offers-table">
                    <thead>
                        <tr>
                            {allColumns.map(column => (
                                visibleColumns.includes(column.id) && <th key={column.id}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, index) => (
                            <motion.tr
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                {allColumns.map(column => (
                                    visibleColumns.includes(column.id) && <td key={column.id}>{row[column.id] || '-'}</td>
                                ))}
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ConversionPage;